import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getFacilityKeyForPortal, getPatientKey } from "@/store/profile";
import { useTaskStore } from "@/store/task";

/** Steps that can be marked complete and skipped on resume. */
export type IntakeStep = "demographics" | "commPrefs" | "financial";

export type IntakeFlags = Partial<Record<IntakeStep, boolean>>;

interface IntakeProgressState {
  byTask: Record<string, IntakeFlags>;
  markDone: (key: string | null, step: IntakeStep) => void;
  isDone: (key: string | null, step: IntakeStep) => boolean;
  getFlags: (key: string | null) => IntakeFlags;
  resetTask: (key: string | null) => void;
}

/**
 * Per-task intake progress, remembered on the device (persisted). Marked only when a step
 * is actually completed in the task — never inferred from server data, so it can't wrongly
 * skip a step. Keyed by facility+patient+task so it never leaks across patients or visits.
 */
export const useIntakeProgressStore = create<IntakeProgressState>()(
  persist(
    (set, get) => ({
      byTask: {},
      markDone: (key, step) => {
        if (!key) return; // no stable task key → do nothing (safe)
        set((s) => ({
          byTask: { ...s.byTask, [key]: { ...s.byTask[key], [step]: true } },
        }));
      },
      isDone: (key, step) => (key ? Boolean(get().byTask[key]?.[step]) : false),
      getFlags: (key) => (key ? get().byTask[key] ?? {} : {}),
      resetTask: (key) => {
        if (!key) return;
        set((s) => {
          const next = { ...s.byTask };
          delete next[key];
          return { byTask: next };
        });
      },
    }),
    { name: "intake-progress" }
  )
);

/**
 * Stable key for the current intake task, or null when no stable task id is available
 * (in which case progress is a no-op and the normal flow runs). Task id comes from
 * activePortalTaskId, which is set for the portal-config (isHistory) flow and persisted.
 */
export function getIntakeTaskKey(
  facilityKey: string = getFacilityKeyForPortal(),
  patientKey: string = getPatientKey(),
  taskId: number | null = useTaskStore.getState().activePortalTaskId
): string | null {
  if (!facilityKey || !patientKey || !taskId || taskId <= 0) return null;
  return `${facilityKey}::${patientKey}::${taskId}`;
}

/** Mark a step done for the current task (no-op if there's no active intake task). */
export function markIntakeStepDone(step: IntakeStep): void {
  useIntakeProgressStore.getState().markDone(getIntakeTaskKey(), step);
}

/** Flags for the current task. */
export function currentIntakeFlags(): IntakeFlags {
  return useIntakeProgressStore.getState().getFlags(getIntakeTaskKey());
}
